import { useEffect } from 'react'
import { ApiError } from '../api/client'
import { type LimitExecutionEvent, fetchLimitExecutionEvents } from '../api/userland'
import { playSound } from '../lib/sound/engine'
import { buyToastLine, sellToastLabel } from '../lib/tradeToastCopy'
import { useAccountSession } from '../wallet/AccountSessionContext'
import {
  type TradeLifecycleToastApi,
  type TradeLifecycleToastHandle,
  useTradeLifecycleToast,
} from './useTradeLifecycleToast'

export const LIMIT_EXECUTION_EVENT = 'wils:limit-execution'

type Attempt = {
  handle: TradeLifecycleToastHandle
  terminal: boolean
  event: LimitExecutionEvent
  started: number
}

function makeRoom(attempts: Map<string, Attempt>) {
  if (attempts.size < 200) return
  const oldest = attempts.keys().next().value
  if (!oldest) return
  attempts.get(oldest)?.handle.dismiss()
  attempts.delete(oldest)
}

function executionContent(event: LimitExecutionEvent, suffix: string) {
  return event.side === 'buy' ? (
    <>Limit order · {buyToastLine(event.ticker, event.amount_label, suffix)}</>
  ) : (
    `Limit order · ${sellToastLabel(event.ticker, event.amount_label)}${suffix}`
  )
}

function updateAttempt(attempt: Attempt, event: LimitExecutionEvent) {
  const content = (suffix: string) => executionContent(event, suffix)
  switch (event.stage) {
    case 'submitted':
      attempt.handle.submitted({ content: content(' · awaiting confirmation') })
      break
    case 'uncertain':
      attempt.handle.pending({ content: content(' · awaiting confirmation · check Activity') })
      break
    case 'confirmed':
      attempt.handle.confirmed({ txHash: event.tx_hash!, content: content(' · confirmed') })
      attempt.terminal = true
      playSound('limitFill', event.attempt_id)
      break
    case 'failed': {
      const reason = event.reason || 'Execution failed'
      attempt.handle.failed({
        reason,
        content: content(` · failed · ${reason}`),
        txHash: event.tx_hash,
      })
      attempt.terminal = true
      playSound('tradeFailure', event.attempt_id)
    }
  }
}

function validEvent(event: LimitExecutionEvent) {
  return (
    Date.now() - event.at <= 10 * 60_000 &&
    event.attempt_id &&
    event.order_id &&
    ['buy', 'sell'].includes(event.side) &&
    ['pending', 'submitted', 'uncertain', 'confirmed', 'failed'].includes(event.stage) &&
    (event.stage !== 'confirmed' || Boolean(event.tx_hash))
  )
}

// One owner in App, independent of the open route or selected wallet. Account
// identity/session changes abort old responses and dismiss their pending toasts.
export function LimitExecutionNotifications() {
  const { status, me } = useAccountSession()
  const lifecycle = useTradeLifecycleToast()
  const accountID = status === 'ready' ? me?.account_id : null
  const sessionID = status === 'ready' ? me?.session_id : null

  useEffect(() => {
    if (!accountID || !sessionID) return
    return subscribeExecutions(lifecycle)
  }, [accountID, sessionID, lifecycle])

  return null
}

function subscribeExecutions(lifecycle: TradeLifecycleToastApi) {
  const abort = new AbortController()
  const attempts = new Map<string, Attempt>()
  let cursor = ''
  let sequence = 0
  let retry: ReturnType<typeof setTimeout> | undefined

  const apply = (event: LimitExecutionEvent) => {
    if (event.sequence <= sequence) return
    sequence = event.sequence
    // An old backlog must not surface as a new trade after a long reconnect.
    if (!validEvent(event)) return
    let attempt = attempts.get(event.attempt_id)
    if (attempt?.terminal) return
    const action = event.side === 'buy' ? 'Buy' : 'Sell'
    const label = `Limit order · ${action} ${event.ticker} ${event.amount_label}`
    if (!attempt) {
      // Bound ownership when many orders execute in a long-lived tab.
      makeRoom(attempts)
      attempt = {
        handle: lifecycle.start({ label, content: executionContent(event, ' · triggered') }),
        terminal: false,
        event,
        started: Date.now(),
      }
      attempts.set(event.attempt_id, attempt)
    }
    updateAttempt(attempt, event)
    window.dispatchEvent(new Event(LIMIT_EXECUTION_EVENT))
  }

  const poll = async () => {
    try {
      const batch = await fetchLimitExecutionEvents(cursor, abort.signal)
      if (abort.signal.aborted) return
      if (batch.reset) sequence = 0
      cursor = batch.cursor
      for (const event of batch.events) apply(event)
      // A normal response waits on the server. The first/reset response is an
      // immediate baseline so historical fills are never announced as new.
      retry = setTimeout(() => void poll(), 0)
    } catch (error) {
      if (abort.signal.aborted) return
      // Do not keep polling a revoked session or an older API without this endpoint.
      if (error instanceof ApiError && [401, 404].includes(error.status)) return
      retry = setTimeout(() => void poll(), 2_000)
    }
  }
  // A missing/restarted confirmation feed is uncertainty, never swap failure.
  const overdue = setInterval(() => {
    for (const attempt of attempts.values()) {
      if (!attempt.terminal && Date.now() - attempt.started >= 60_000) {
        attempt.handle.pending({
          content: executionContent(attempt.event, ' · awaiting confirmation · check Activity'),
        })
      }
    }
  }, 15_000)
  void poll()
  return () => {
    abort.abort()
    clearTimeout(retry)
    clearInterval(overdue)
    for (const attempt of attempts.values()) attempt.handle.dismiss()
  }
}
